/**
 * 套餐相关的 TanStack Query Hooks
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { getPackageList } from '@/services/packageService'
import { transformPackageList } from '@/lib/packageUtils'
import type { Package, PackageListParams } from '@/types/package'

/** 套餐列表查询 Key */
const PACKAGES_QUERY_KEY = ['packages'] as const

/**
 * 获取套餐列表的 Hook
 * @param params 查询参数
 * @returns 套餐列表查询结果
 */
export function usePackages(
  params?: PackageListParams
): UseQueryResult<Package[]> {
  return useQuery({
    queryKey: [...PACKAGES_QUERY_KEY, params] as const,
    queryFn: async (): Promise<Package[]> => {
      const data = await getPackageList(params)
      return transformPackageList(data.list)
    },
  })
}

/**
 * 分组后的套餐数据
 */
export interface GroupedPackages {
  /** 7天会员套餐 */
  weekly: Package[]
  /** 月付订阅套餐 */
  monthly: Package[]
  /** 季会员套餐 */
  quarterly: Package[]
  /** 年付订阅套餐 */
  yearly: Package[]
  /** 会员套餐（7天/月/季/年） */
  subscription: Package[]
  /** 客户端席位套餐 */
  clientSeat: Package[]
  /** Plus 席位套餐 */
  clientPlus: Package[]
  /** 算力包（一次性购买） */
  recharge: Package[]
}

/**
 * 获取所有套餐并按类型分组的 Hook
 * @returns 分组后的套餐数据
 */
export function useAllPackages(): UseQueryResult<GroupedPackages> {
  return useQuery({
    queryKey: [...PACKAGES_QUERY_KEY, 'all', 'grouped'] as const,
    queryFn: async (): Promise<GroupedPackages> => {
      const data = await getPackageList({
        pageSize: 100,
      })
      const allPackages = transformPackageList(data.list)

      // 按套餐类型分组并排序
      const sortByOrder = (a: Package, b: Package): number => a.sortOrder - b.sortOrder
      const weekly = allPackages
        .filter((pkg) => pkg.packageType === 'weekly')
        .sort(sortByOrder)
      const monthly = allPackages
        .filter((pkg) => pkg.packageType === 'monthly')
        .sort(sortByOrder)
      const quarterly = allPackages
        .filter((pkg) => pkg.packageType === 'quarterly')
        .sort(sortByOrder)
      const yearly = allPackages
        .filter((pkg) => pkg.packageType === 'yearly')
        .sort(sortByOrder)

      return {
        weekly,
        monthly,
        quarterly,
        yearly,
        subscription: [...weekly, ...monthly, ...quarterly, ...yearly].sort(sortByOrder),
        clientSeat: allPackages
          .filter((pkg) => pkg.packageType === 'client_seat')
          .sort(sortByOrder),
        clientPlus: allPackages
          .filter((pkg) => pkg.packageType === 'client_plus')
          .sort(sortByOrder),
        recharge: allPackages
          .filter((pkg) => pkg.packageType === 'recharge')
          .sort(sortByOrder),
      }
    },
  })
}

export { PACKAGES_QUERY_KEY }
